import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Modal, Alert } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api'
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

const QuizEditor = () => {
    const { id, studyCircleId, folderId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [studyCircles, setStudyCircles] = useState([]);
    const [folders, setFolders] = useState([]);
    const [quiz, setQuiz] = useState({
        title: '',
        description: '',
        study_circle_id: '',
        folder_id: '',
        time_limit: 100,
        passing_score: 80,
        allowed_attempts: 1,
        start_date: '',
        start_time: '',
        end_date: '',
        end_time: '',
        questions: []
    });
    const [showQuestionModal, setShowQuestionModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [importStudyCircle, setImportStudyCircle] = useState('');
    const [importFolder, setImportFolder] = useState('');
    const [importQuiz, setImportQuiz] = useState('');
    const [importFolders, setImportFolders] = useState([]);
    const [importQuizzes, setImportQuizzes] = useState([]);
    const [isImporting, setIsImporting] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState({
        question_text: '',
        points: 1,
        answers: [
            { answer_text: '', is_correct: false },
            { answer_text: '', is_correct: false },
            { answer_text: '', is_correct: false },
            { answer_text: '', is_correct: false }
        ]
    });
    const [expandedQuestion, setExpandedQuestion] = useState(null);

    // Add Quill modules configuration
    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'script': 'super'}, { 'script': 'sub' }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['link', 'image'],
            ['clean']
        ]
    };

    const quillFormats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'script',
        'list', 'bullet',
        'link', 'image'
    ];

    // Add date handling functions
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
    };

    const formatTimeForInput = (timeString) => {
        if (!timeString) return '';
        return timeString;
    };

    const combineDateAndTime = (date, time) => {
        if (!date || !time) return null;
        const [hours, minutes] = time.split(':');
        const combinedDate = new Date(date);
        combinedDate.setHours(parseInt(hours), parseInt(minutes));
        return combinedDate.toISOString();
    };

    useEffect(() => {
        fetchStudyCircles();
        if (id) {
            fetchQuiz();
        } else {
            // Reset form when navigating to create new quiz
            setQuiz({
                title: '',
                description: '',
                study_circle_id: studyCircleId ?? '',
                folder_id: folderId ?? '',
                time_limit: 100,
                passing_score: 80,
                allowed_attempts: 1,
                start_date: '',
                start_time: '',
                end_date: '',
                end_time: '',
                questions: []
            });
            setLoading(false);
        }
    }, [id]);

    // Cleanup effect when component unmounts
    useEffect(() => {
        return () => {
            setQuiz({
                title: '',
                description: '',
                study_circle_id: '',
                folder_id: '',
                time_limit: 100,
                passing_score: 80,
                allowed_attempts: 1,
                start_date: '',
                start_time: '',
                end_date: '',
                end_time: '',
                questions: []
            });
        };
    }, []);

    const fetchStudyCircles = async () => {
        try {
            const response = await api.get('/api/study-circles');
            setStudyCircles(response.data);
        } catch (err) {
            setError('Failed to fetch study circles');
            console.error('Error fetching study circles:', err);
        }
    };

    const fetchFolders = async (studyCircleId) => {
        try {
            const response = await api.get(`/api/study-circles/${studyCircleId}/folders`);
            setFolders(response.data);
        } catch (err) {
            setError('Failed to fetch folders');
            console.error('Error fetching folders:', err);
        }
    };

    useEffect(() => {
        if (quiz.study_circle_id) {
            fetchFolders(quiz.study_circle_id);
        }
    }, [quiz.study_circle_id]);

    const fetchQuiz = async () => {
        try {
            const response = await api.get(`/api/quizzes/${id}`);
            const quizData = response.data;
            setQuiz({
                ...quizData,
                start_date: formatDateForInput(quizData.start_date),
                start_time: formatTimeForInput(quizData.start_time),
                end_date: formatDateForInput(quizData.end_date),
                end_time: formatTimeForInput(quizData.end_time)
            });
        } catch (err) {
            setError('Failed to fetch quiz');
            console.error('Error fetching quiz:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        try {
            const quizData = {
                ...quiz,
                start_date: quiz.start_date || null,
                start_time: quiz.start_time || null,
                end_date: quiz.end_date || null,
                end_time: quiz.end_time || null
            };

            if (id) {
                await api.put(`/api/quizzes/${id}`, quizData);
            } else {
                await api.post('/api/quizzes', quizData);
            }
            navigate('/quizzes');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save quiz');
            console.error('Error saving quiz:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleQuestionChange = (index, field, value) => {
        const newQuestions = [...quiz.questions];
        newQuestions[index] = { ...newQuestions[index], [field]: value };
        setQuiz({ ...quiz, questions: newQuestions });
    };

    const handleAnswerChange = (questionIndex, answerIndex, field, value) => {
        const newQuestions = [...quiz.questions];
        newQuestions[questionIndex].answers[answerIndex] = {
            ...newQuestions[questionIndex].answers[answerIndex],
            [field]: value
        };
        setQuiz({ ...quiz, questions: newQuestions });
    };

    const addQuestion = () => {
        setQuiz({
            ...quiz,
            questions: [
                ...quiz.questions,
                {
                    question_text: '',
                    points: 1,
                    answers: [
                        { answer_text: '', is_correct: true },
                        { answer_text: '', is_correct: false },
                        { answer_text: '', is_correct: false },
                        { answer_text: '', is_correct: false }
                    ]
                }
            ]
        });
    };

    const removeQuestion = (index) => {
        const newQuestions = [...quiz.questions];
        newQuestions.splice(index, 1);
        setQuiz({ ...quiz, questions: newQuestions });
    };

    const handleImportStudyCircleChange = async (e) => {
        const studyCircleId = e.target.value;
        setImportStudyCircle(studyCircleId);
        setImportFolder('');
        setImportQuiz('');
        if (studyCircleId) {
            try {
                const response = await api.get(`/api/study-circles/${studyCircleId}/folders`);
                setImportFolders(response.data);
            } catch (err) {
                setError('Failed to fetch folders');
                console.error('Error fetching folders:', err);
            }
        } else {
            setImportFolders([]);
        }
    };

    const handleImportFolderChange = async (e) => {
        const folderId = e.target.value;
        setImportFolder(folderId);
        setImportQuiz('');
        if (folderId) {
            try {
                const response = await api.get(`/api/quizzes/folder/${folderId}`);
                setImportQuizzes(response.data);
            } catch (err) {
                setError('Failed to fetch quizzes');
                console.error('Error fetching quizzes:', err);
            }
        } else {
            setImportQuizzes([]);
        }
    };

    const handleImportQuiz = async () => {
        if (!importQuiz) {
            setError('Please select a quiz to import');
            return;
        }
        setIsImporting(true);
        setError('');

        try {
            const response = await api.get(`/api/quizzes/${importQuiz}`);
            const importedQuiz = response.data;

            // Add imported questions to current quiz
            const newQuestions = [...quiz.questions, ...importedQuiz.questions.map(({ id, answers, ...q }) => ({
                ...q,
                answers: answers.map(({ id, ...a }) => a)
              }))];
            setQuiz({ ...quiz, questions: newQuestions });
            setShowImportModal(false);
            setImportStudyCircle('');
            setImportFolder('');
            setImportQuiz('');
            setImportFolders([]);
            setImportQuizzes([]);
        } catch (err) {
            setError('Failed to import quiz');
            console.error('Error importing quiz:', err);
        } finally {
            setIsImporting(false);
        }
    };

    if (loading) {
        return <div className="text-center mt-5">Loading...</div>;
    }

    return (
        <div className="container mt-4">
            <h2>{id ? 'Edit Quiz' : 'Create New Quiz'}</h2>
            {error && <div className="alert alert-danger">{error}</div>}

            <form onSubmit={handleSubmit}>
                {/* place first two cards side by side */}
                
                <div className="d-flex flex-column flex-md-row justify-content-between gap-3">
                    <div className="card mb-4">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <label htmlFor="title" className="form-label">Title</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="title"
                                        value={quiz.title}
                                        onChange={(e) => setQuiz({ ...quiz, title: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className="col-12 mb-3">
                                    <label htmlFor="description" className="form-label">Description</label>
                                    <ReactQuill
                                        value={quiz.description}
                                        onChange={(content) => setQuiz({ ...quiz, description: content })}
                                        modules={quillModules}
                                        formats={quillFormats}
                                        className="quiz-editor-quill"
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label htmlFor="startDate" className="form-label">Start Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        id="startDate"
                                        value={quiz.start_date}
                                        onChange={(e) => setQuiz({ ...quiz, start_date: e.target.value })}
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label htmlFor="startTime" className="form-label">Start Time</label>
                                    <input
                                        type="time"
                                        className="form-control"
                                        id="startTime"
                                        value={quiz.start_time}
                                        onChange={(e) => setQuiz({ ...quiz, start_time: e.target.value })}
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label htmlFor="endDate" className="form-label">End Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        id="endDate"
                                        value={quiz.end_date}
                                        onChange={(e) => setQuiz({ ...quiz, end_date: e.target.value })}
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label htmlFor="endTime" className="form-label">End Time</label>
                                    <input
                                        type="time"
                                        className="form-control"
                                        id="endTime"
                                        value={quiz.end_time}
                                        onChange={(e) => setQuiz({ ...quiz, end_time: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="card mb-4">
                        <div className="card-body bg-gray-100">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label htmlFor="studyCircle" className="form-label">Study Circle</label>
                                    <select
                                        className="form-select"
                                        id="studyCircle"
                                        value={quiz.study_circle_id}
                                        onChange={(e) => {
                                            setQuiz({ 
                                                ...quiz, 
                                                study_circle_id: e.target.value,
                                                folder_id: '' // Reset folder when study circle changes
                                            });
                                        }}
                                        required
                                    >
                                        <option value="">Select Study Circle</option>
                                        {studyCircles.map(circle => (
                                            <option key={circle.id} value={circle.id}>
                                                {circle.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label htmlFor="folder" className="form-label">Folder</label>
                                    <select
                                        className="form-select"
                                        id="folder"
                                        value={quiz.folder_id}
                                        onChange={(e) => setQuiz({ ...quiz, folder_id: e.target.value })}
                                        disabled={!quiz.study_circle_id}
                                        required
                                    >
                                        <option value="">No Folder</option>
                                        {folders.map(folder => (
                                            <option key={folder.id} value={folder.id}>
                                                {folder.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-md-6 mb-3">
                                    <div className="mb-3">
                                        <label htmlFor="time_limit" className="form-label">Time Limit (minutes)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id="time_limit"
                                            value={quiz.time_limit}
                                            onChange={(e) => setQuiz({ ...quiz, time_limit: parseInt(e.target.value) || 0 })}
                                            min="1"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label htmlFor="passing_score" className="form-label">Passing Score (%)</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        id="passing_score"
                                        value={quiz.passing_score}
                                        onChange={(e) => setQuiz({ ...quiz, passing_score: parseInt(e.target.value) || 0 })}
                                        min="0"
                                        max="100"
                                        required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label htmlFor="allowed_attempts" className="form-label">Allowed Attempts</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        id="allowed_attempts"
                                        value={quiz.allowed_attempts}
                                        onChange={(e) => setQuiz({ ...quiz, allowed_attempts: parseInt(e.target.value) || 0 })}
                                        min="1"
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card mb-4">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h3 className="h5 mb-0">Questions</h3>
                        <div>
                            <Button
                                variant="primary"
                                className="me-2"
                                onClick={() => setShowImportModal(true)}
                            >
                                Import Questions
                            </Button>
                            <Button
                                variant="primary"
                                onClick={addQuestion}
                            >
                                Add Question
                            </Button>
                        </div>
                    </div>
                    <div className="card-body">
                        {quiz.questions.length === 0 && (
                            <div className="text-center text-muted">No questions yet.</div>
                        )}
                        {quiz.questions.map((question, index) => (
                            <div key={index} className="card mb-3">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="d-flex align-items-center">
                                            <span className="me-2">Question {index + 1}</span>
                                            <button
                                                type="button"
                                                className="btn btn-link p-0"
                                                onClick={() => setExpandedQuestion(expandedQuestion === index ? null : index)}
                                                aria-label={expandedQuestion === index ? 'Collapse' : 'Expand'}
                                            >
                                                {expandedQuestion === index ? <FaChevronUp /> : <FaChevronDown />}
                                            </button>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-danger btn-sm"
                                            onClick={() => removeQuestion(index)}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                    {expandedQuestion === index ? (
                                        <>
                                            <div className="mb-4">
                                                <label className="form-label">Question Text</label>
                                                <ReactQuill
                                                    value={question.question_text}
                                                    onChange={(content) => handleQuestionChange(index, 'question_text', content)}
                                                    modules={quillModules}
                                                    formats={quillFormats}
                                                    className="quiz-editor-quill"
                                                />
                                            </div>
                                            <div className="mb-4">
                                                <label className="form-label">Points</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={question.points}
                                                    onChange={(e) => handleQuestionChange(index, 'points', parseInt(e.target.value))}
                                                    min="1"
                                                    required
                                                    style={{ width: '100px' }}
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Answers</label>
                                                {question.answers.map((answer, answerIndex) => (
                                                    <div key={answerIndex} className="mb-4">
                                                        <div className="d-flex align-items-start mb-2">
                                                            <div className="input-group-text me-2">
                                                                <input
                                                                    type="radio"
                                                                    name={`correct-${index}`}
                                                                    checked={answer.is_correct}
                                                                    onChange={() => {
                                                                        const newAnswers = question.answers.map((a, i) => ({
                                                                            ...a,
                                                                            is_correct: i === answerIndex
                                                                        }));
                                                                        handleQuestionChange(index, 'answers', newAnswers);
                                                                    }}
                                                                />
                                                            </div>
                                                            <div className="flex-grow-1">
                                                                <ReactQuill
                                                                    value={answer.answer_text}
                                                                    onChange={(content) => handleAnswerChange(index, answerIndex, 'answer_text', content)}
                                                                    modules={quillModules}
                                                                    formats={quillFormats}
                                                                    className="mb-4 quiz-editor-quill"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                                            <span dangerouslySetInnerHTML={{ __html: question.question_text || '<i>No question text</i>' }} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="d-flex justify-content-end gap-2">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => navigate('/quizzes')}
                    >
                        Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                        {isSubmitting ? 'Saving...' : (id ? 'Update Quiz' : 'Create Quiz')}
                    </button>
                </div>
            </form>

            {/* Import Questions Modal */}
            <Modal show={showImportModal} onHide={() => setShowImportModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Import Questions</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Study Circle</Form.Label>
                            <Form.Select
                                value={importStudyCircle}
                                onChange={handleImportStudyCircleChange}
                            >
                                <option value="">Select Study Circle</option>
                                {studyCircles.map(circle => (
                                    <option key={circle.id} value={circle.id}>
                                        {circle.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Folder</Form.Label>
                            <Form.Select
                                value={importFolder}
                                onChange={handleImportFolderChange}
                                disabled={!importStudyCircle}
                            >
                                <option value="">Select Folder</option>
                                {importFolders.map(folder => (
                                    <option key={folder.id} value={folder.id}>
                                        {folder.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Quiz</Form.Label>
                            <Form.Select
                                value={importQuiz}
                                onChange={(e) => setImportQuiz(e.target.value)}
                                disabled={!importFolder}
                            >
                                <option value="">Select Quiz</option>
                                {importQuizzes.map(quiz => (
                                    <option key={quiz.id} value={quiz.id}>
                                        {quiz.title}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowImportModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleImportQuiz} disabled={isImporting}>
                        {isImporting ? 'Importing...' : 'Import'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default QuizEditor; 